import { createMultipleFilter, createSingleFilter } from "app/utils/filters";

import type { GroupBandsGridGroupsQuery } from "./__generated__/GroupBandsGridGroupsQuery.graphql";

/** Which slice of a group's identity data an expanded row shows. */
export type GroupsView = "role-assignments" | "users";

/** How a band selects its groups: one provider's namespace, or all local groups. */
export type BandScope =
  | { kind: "idp-namespace"; idpNamespace: string }
  | { kind: "local" };

/** One section of the groups browser: a provider's groups, or the local ones. */
export interface GroupBand {
  /** Stable id (the provider's Relay id, or a fixed key for the local band). */
  id: string;
  /** Band label, e.g. "Okta" / "AWS" / "Local Groups". */
  label: string;
  /** Short secondary label after the band name (the namespace or org key). */
  secondary?: string | null;
  /** Selects the band's groups. */
  scope: BandScope;
  /** Provider `idpType`, which picks each group's logo; omit for the local band. */
  idpType?: string | null;
  /** Provider label for each group's "Synced from" line; omit for the local band. */
  idpDisplayName?: string | null;
  /** Details-page link for the band's arrow action, or null when there is none. */
  to?: string | null;
  /** Edit-page link for the band's ✎ action, or null to omit it. */
  editTo?: string | null;
}

/** A single group row beneath a band. */
export interface GroupRow {
  id: string;
  uuid: string;
  /** The group's stable name, which scopes its members list. */
  name: string;
  label: string;
  /** Direct member count, when the API reports one. */
  memberCount: number | null;
  /** Whether the group grants the admin role. */
  hasAdmin: boolean;
  /** Account groups the group's resource-viewer role is scoped to. */
  visibilityTargets: string[];
  /**
   * True when the group has more role assignments than were fetched (capped),
   * so the access summary and expanded table are incomplete.
   */
  hasMoreAssignments: boolean;
  /** The group node, handed to the expanded panel's role assignment table. */
  principalRef: NonNullable<
    NonNullable<
      GroupBandsGridGroupsQuery["response"]["userGroups"]["edges"]
    >[number]
  >["node"];
}

/**
 * Scopes the list to a band's groups, optionally narrowed by a name search.
 *
 * Provider bands match by exact `idp-namespace` — its default `contains`
 * operator would otherwise leak groups from any provider whose namespace merely
 * contains this one. The local band matches `system: false`; `idp-namespace`
 * can't select local groups, as one with no provider matches no namespace.
 */
export function buildFilter(scope: BandScope, search: string) {
  const term = search.trim();
  const scopeFilter =
    scope.kind === "local"
      ? createSingleFilter("system", "false")
      : createSingleFilter("idp-namespace", scope.idpNamespace, "equals");
  return createMultipleFilter([
    scopeFilter,
    term ? createSingleFilter("name", term, "contains") : undefined,
  ]);
}
